import { NotificationSeverity } from '@/components/notifications/Notification';
import { useParams, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useNotification } from '@/hooks';
import {
  useGetOpenReturnsSummaryQuery,
  useCloseReturnsIntakeByDriverMutation,
} from '../../services/returnsIntake.endpoints';

const COMPLIANCE_MAP: Record<string, string> = {
  RETURNED: 'returned',
  RETURNED_CANCELLED: 'returned',
  UNEXPECTED_RETURN: 'unexpected',
  UNEXPECTED_RETURN_DELIVERED: 'unexpected',
  NOT_REPORTED: 'nonReported',
};

const TAB_INDEX_TO_KEY = [
  'returned',
  'unexpected',
  'nonReported',
];

const useReturnsIntakeScannerSummary = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  const { showNotification } = useNotification();

  const { data, isLoading, isError, refetch } = useGetOpenReturnsSummaryQuery(
    { driverId: driverId || '' },
    { skip: !driverId }
  );

  const [closeRendition, { isLoading: isClosing }] =
    useCloseReturnsIntakeByDriverMutation();

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const filteredShippings = useMemo(() => {
    if (!data?.shippings) return [];
    return data.shippings.filter((s) => {
      const name = s.complianceName?.toUpperCase() || '';
      const group = COMPLIANCE_MAP[name];
      if (!group) return false;
      return group === TAB_INDEX_TO_KEY[activeTab];
    });
  }, [data, activeTab]);

  const handleFinish = async () => {
    if (!driverId) return;
    setIsFinishing(true);
    try {
      await closeRendition({ driverId }).unwrap();
      localStorage.removeItem('returns_scanner_items');
      localStorage.removeItem('returns_scanner_driver');
      showNotification(
        'Rendición cerrada correctamente',
        NotificationSeverity.success
      );
      navigate('/business/returns-intake');
    } catch (error) {
      showNotification(
        'Error al cerrar la rendición',
        NotificationSeverity.error
      );
      setIsFinishing(false);
    }
  };

  const handleBack = () => {
    navigate('/business/returns-intake/scanner');
  };

  const handleGoToStart = () => {
    navigate('/business/returns-intake');
  };

  const driverName = useMemo(
    () => data?.shippings?.[0]?.driverName || '',
    [data]
  );

  const stats = useMemo(() => {
    const counts = {
      returned: 0,
      unexpected: 0,
      nonReported: 0,
    };
    const explanations = {
      returned: '',
      unexpected: '',
      nonReported: '',
    };

    if (!data?.shippings) return { counts, explanations };

    data.shippings.forEach((s) => {
      const name = s.complianceName?.toUpperCase() || '';
      const exp = s.complianceCommercialExplanation || '';
      const key = COMPLIANCE_MAP[name] as keyof typeof counts;

      if (key && counts.hasOwnProperty(key)) {
        counts[key]++;
        if (!explanations[key]) explanations[key] = exp;
      }
    });

    return { counts, explanations };
  }, [data]);

  return {
    shippings: filteredShippings,
    driverName,
    driverId,
    stats: stats.counts,
    explanations: stats.explanations,
    isLoading: isLoading || isClosing || isFinishing,
    isError,
    activeTab,
    setActiveTab,
    handleTabChange,
    handleFinish,
    handleBack,
    handleGoToStart,
    refetch,
  };
};

export default useReturnsIntakeScannerSummary;
